/* eslint-disable @typescript-eslint/no-explicit-any */
import { type KeyMap } from 'helpers/interface';

import { eventCollectorClient } from './connectivity/ml-gateway-api/event-collector/client';
import type { MLGatewaySendEvent } from './connectivity/ml-gateway-api/event-collector/types';

type UniqueId = string | number;

type ConstructorProps<D extends KeyMap<any>, E extends MLGatewaySendEvent> = {
  initialState: Record<UniqueId, D>;
  eventValidator: (event?: D) => boolean;
  prepareEvent: (data: D) => E;
};

export class EventCollector<D extends KeyMap<any>, E extends MLGatewaySendEvent> {
  private state: Record<UniqueId, D>;
  private eventValidator: (event?: D) => boolean;
  private prepareEvent: (data: D) => E;

  constructor({ initialState, eventValidator, prepareEvent }: ConstructorProps<D, E>) {
    this.state = initialState;
    this.eventValidator = eventValidator;
    this.prepareEvent = prepareEvent;
  }

  public addEvent(uniqueId: UniqueId, data: D): this {
    this.state[uniqueId] = data;

    return this;
  }

  private dumpEvent(uniqueId: UniqueId): this {
    delete this.state[uniqueId];

    return this;
  }

  public updateEvent(uniqueId: UniqueId, data: D extends (infer R)[] ? Partial<R>[] : Partial<D>): this {
    if (!this.state[uniqueId]) {
      return this;
    }

    const currentPayload = this.state[uniqueId];

    if (Array.isArray(currentPayload)) {
      const updatedEvent = [...currentPayload, ...(data as unknown as Partial<D>[])] as unknown as D;
      this.state[uniqueId] = updatedEvent;

      return this;
    }

    const updatedEvent = {
      ...currentPayload,
      ...data,
    } as D;

    this.state[uniqueId] = updatedEvent;

    return this;
  }

  public async sendEvent(uniqueId: UniqueId): Promise<void> {
    if (!this.state[uniqueId]) {
      return;
    }

    const currentEvent = this.state[uniqueId];

    if (!this.eventValidator(currentEvent)) {
      this.dumpEvent(uniqueId);

      return;
    }

    const eventToSend = this.prepareEvent(currentEvent);

    try {
      // dump event before sending to avoid situation when event could be sent multiple times due to some network issues
      this.dumpEvent(uniqueId);

      await eventCollectorClient.sendEvent(eventToSend);
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to send custom event');
    }
  }
}
